import { supabase } from '../supabase/supabaseClient.js';
import { calculatePlanMetrics } from './planMetricsService.js';

// Servicio especializado en métricas básicas de la compañía usando vistas de Supabase
export const calculateBasicMetrics = async (companyId) => {
  try {
    console.log('Calculando métricas básicas para compañía:', companyId);

    // 1. Obtener métricas básicas de la vista company_basic_metrics
    const { data: basicMetrics, error: basicError } = await supabase
      .from('company_basic_metrics')
      .select('*')
      .eq('id', companyId)
      .single();

    if (basicError) {
      console.error('Error obteniendo métricas básicas:', basicError);
    }

    // 2. Obtener datos de planes de la compañía
    const { data: planes, error: planesError } = await supabase
      .from('company_planes')
      .select('*')
      .eq('compania_id', companyId);

    if (planesError) {
      console.error('Error obteniendo planes:', planesError);
    }

    // 3. Obtener cotizaciones de la compañía
    const { data: cotizaciones, error: cotizacionesError } = await supabase
      .from('company_cotizaciones')
      .select('*')
      .eq('compania_id', companyId);

    if (cotizacionesError) {
      console.error('Error obteniendo cotizaciones:', cotizacionesError);
    }

    // 4. Obtener contrataciones de la compañía
    const { data: contrataciones, error: contratacionesError } = await supabase
      .from('company_contrataciones')
      .select('*')
      .eq('compania_id', companyId);

    if (contratacionesError) {
      console.error('Error obteniendo contrataciones:', contratacionesError);
    }

    // 5. Obtener pagos de la compañía
    const { data: pagos, error: pagosError } = await supabase
      .from('company_pagos')
      .select('*')
      .eq('compania_id', companyId);

    if (pagosError) {
      console.error('Error obteniendo pagos:', pagosError);
    }

    // 6. Obtener órdenes de pago de la compañía
    const { data: ordenesPago, error: ordenesError } = await supabase
      .from('company_ordenes_pago')
      .select('*')
      .eq('compania_id', companyId);

    if (ordenesError) {
      console.error('Error obteniendo órdenes de pago:', ordenesError);
    }

    // 7. Obtener certificados de la compañía
    const { data: certificados, error: certificadosError } = await supabase
      .from('company_certificados')
      .select('*')
      .eq('compania_id', companyId);

    if (certificadosError) {
      console.error('Error obteniendo certificados:', certificadosError);
    }

    // 8. Obtener suscripciones de la compañía
    const { data: suscripciones, error: suscripcionesError } = await supabase
      .from('company_suscripciones')
      .select('*')
      .eq('company_id', companyId);

    if (suscripcionesError) {
      console.error('Error obteniendo suscripciones:', suscripcionesError);
    }

    // 9. Obtener sesiones de la compañía
    const { data: sesiones, error: sesionesError } = await supabase
      .from('company_sesiones')
      .select('*')
      .eq('compania_id', companyId);

    if (sesionesError) {
      console.error('Error obteniendo sesiones:', sesionesError);
    }

    // Obtener métricas de planes específicos
    let planMetrics = null;
    try {
      planMetrics = await calculatePlanMetrics(companyId);
    } catch (planError) {
      console.error('Error calculando métricas de planes:', planError);
      planMetrics = {
        resumen_actividad: {
          planes_con_sesiones_count: 0,
          planes_con_simulaciones_count: 0,
          planes_con_contrataciones_count: 0,
          porcentaje_planes_activos: '0.00'
        },
        total_planes_con_actividad: 0
      };
    }

    // Validar datos con fallbacks seguros
    const safePlanes = Array.isArray(planes) ? planes : [];
    const safeCotizaciones = Array.isArray(cotizaciones) ? cotizaciones : [];
    const safeContrataciones = Array.isArray(contrataciones) ? contrataciones : [];
    const safePagos = Array.isArray(pagos) ? pagos : [];
    const safeOrdenesPago = Array.isArray(ordenesPago) ? ordenesPago : [];
    const safeCertificados = Array.isArray(certificados) ? certificados : [];
    const safeSuscripciones = Array.isArray(suscripciones) ? suscripciones : [];
    const safeSesiones = Array.isArray(sesiones) ? sesiones : [];

    // Calcular métricas derivadas
    const planesActivos = safePlanes.filter(p => p?.activo === true).length;
    const contratacionesPendientes = safeContrataciones.filter(c => c?.estado === 'pendiente').length;
    const ordenesPayPendientes = safeOrdenesPago.filter(o => o?.estado === 'pendiente').length;
    const pagosExitosos = safePagos.filter(p => p?.status === 'approved' || p?.status === 'paid').length;
    const suscripcionesActivas = safeSuscripciones.filter(s => s?.status === 'activa' || s?.status === 'active').length;
    const sesionesConSimulacion = safeSesiones.filter(s => s?.datos_simulacion).length;

    // Calcular montos totales
    const montoTotalOrdenes = safeOrdenesPago.reduce((sum, orden) => sum + (parseFloat(orden?.monto) || 0), 0);
    const montoTotalPagos = safePagos.reduce((sum, pago) => sum + (parseFloat(pago?.amount) || 0), 0);

    // Calcular tasas de conversión
    const tasaCotizacionContratacion = safeCotizaciones.length > 0 ? 
      ((safeContrataciones.length / safeCotizaciones.length) * 100).toFixed(2) : '0.00';
    const tasaContratacionPago = safeContrataciones.length > 0 ? 
      ((pagosExitosos / safeContrataciones.length) * 100).toFixed(2) : '0.00';

    // Construir respuesta usando datos de las vistas y cálculos derivados
    return {
      // Métricas de productos (desde company_basic_metrics o calculadas)
      total_productos: basicMetrics?.total_productos || 0,
      productos_activos: basicMetrics?.productos_activos || 0,
      
      // Métricas de planes
      total_planes: safePlanes.length,
      planes_activos: planesActivos,
      
      // Métricas de planes específicos
      planes_con_sesiones_count: planMetrics?.resumen_actividad?.planes_con_sesiones_count || 0,
      planes_con_simulaciones_count: planMetrics?.resumen_actividad?.planes_con_simulaciones_count || 0,
      planes_con_contrataciones_count: planMetrics?.resumen_actividad?.planes_con_contrataciones_count || 0,
      total_planes_con_actividad: planMetrics?.total_planes_con_actividad || 0,
      porcentaje_planes_activos: planMetrics?.resumen_actividad?.porcentaje_planes_activos || '0.00',
      planes_detalle: planMetrics,
      
      // Métricas de conversión
      total_sesiones: safeSesiones.length,
      total_simulaciones: sesionesConSimulacion,
      total_cotizaciones: safeCotizaciones.length,
      total_contrataciones: safeContrataciones.length,
      contrataciones_pendientes: contratacionesPendientes,
      
      // Métricas de pagos
      total_ordenes_pago: safeOrdenesPago.length,
      ordenes_pago_pendientes: ordenesPayPendientes,
      total_pagos: safePagos.length,
      pagos_exitosos: pagosExitosos,
      
      // Métricas de certificados y suscripciones
      total_certificados: safeCertificados.length,
      total_subscriptions: safeSuscripciones.length,
      subscriptions_activas: suscripcionesActivas,
      total_cuotas: 0, // No hay vista específica para cuotas
      
      // Métricas financieras
      monto_total_ordenes: montoTotalOrdenes,
      monto_total_pagos: montoTotalPagos,
      
      // Tasas de conversión
      tasa_cotizacion_contratacion: tasaCotizacionContratacion,
      tasa_contratacion_pago: tasaContratacionPago,
      
      // Información de suscripción
      subscription_status: safeSuscripciones.length > 0 ? 
        (safeSuscripciones[0]?.status || 'inactive') : 'inactive',
      subscription_plan: safeSuscripciones.length > 0 ? 
        (safeSuscripciones[0]?.plan_name || 'none') : 'none',
      
      // Revenue desde company_basic_metrics
      revenue: basicMetrics?.revenue || 0,
      clients: basicMetrics?.clients || 0,
      policies: basicMetrics?.policies || 0,
      
      last_sync: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error calculating basic metrics:', error);
    throw error;
  }
};

export default {
  calculateBasicMetrics
};